import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Home, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const { t, language } = useLanguage();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [order, setOrder] = useState(null);

  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      // PayPal is the only active flow; legacy Stripe path was removed.
      setStatus('success');
      clearCart();
      if (orderId) {
        try {
          const orderResponse = await axios.get(`${API}/orders/${orderId}`);
          setOrder(orderResponse.data);
        } catch (err) {
          console.warn('Failed to fetch order details:', err);
        }
      }
    };

    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const generateWhatsAppMessage = () => {
    if (!order) return '';
    
    const itemsList = order.items.map(item => {
      const name = item[`name_${language}`] || item.name_es;
      return `• ${item.quantity}x ${name} - $${item.subtotal.toFixed(2)}`;
    }).join('\n');

    const messages = {
      es: `Nuevo pedido de Jabad GDL Copa Mundial 2026!\n\n Pedido #${order.id.slice(0, 8)}\n\n Platillos:\n${itemsList}\n\n Total: $${order.total_amount.toFixed(2)} MXN\n${order.delivery_cost ? `Envio: $${order.delivery_cost.toFixed(2)} MXN\n` : ''}\n ${order.is_pickup ? 'RECOGER EN TIENDA' : `Direccion: ${order.delivery_address}`}\n\n Cliente: ${order.customer_name}\n Telefono: ${order.customer_phone}\n\n${order.notes ? `Notas: ${order.notes}` : ''}`,
      en: `New order from Jabad GDL World Cup 2026!\n\n Order #${order.id.slice(0, 8)}\n\n Items:\n${itemsList}\n\n Total: $${order.total_amount.toFixed(2)} MXN\n${order.delivery_cost ? `Delivery: $${order.delivery_cost.toFixed(2)} MXN\n` : ''}\n ${order.is_pickup ? 'STORE PICKUP' : `Address: ${order.delivery_address}`}\n\n Customer: ${order.customer_name}\n Phone: ${order.customer_phone}\n\n${order.notes ? `Notes: ${order.notes}` : ''}`,
      he: `הזמנה חדשה מחב"ד גואדלחרה מונדיאל 2026!\n\n הזמנה #${order.id.slice(0, 8)}\n\n מנות:\n${itemsList}\n\n סה"כ: $${order.total_amount.toFixed(2)} MXN\n\n ${order.is_pickup ? 'איסוף מהחנות' : `כתובת: ${order.delivery_address}`}\n\n לקוח: ${order.customer_name}\n טלפון: ${order.customer_phone}\n\n${order.notes ? `הערות: ${order.notes}` : ''}`
    };

    return encodeURIComponent(messages[language] || messages.es);
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-20 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4 bg-white border-[#E5E3D8] rounded-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-[#B85042] mx-auto mb-4" />
            <p className="font-body text-[#5C6C62]">{t('processing')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-20 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4 bg-white border-[#E5E3D8] rounded-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0F2922] mb-2">
              {t('error')}
            </h2>
            <p className="font-body text-[#5C6C62] mb-6">
              {language === 'es' 
                ? 'Hubo un problema con tu pago. Por favor intenta de nuevo.'
                : language === 'en'
                ? 'There was a problem with your payment. Please try again.'
                : 'הייתה בעיה עם התשלום שלך. אנא נסה שוב.'}
            </p>
            <Link to="/kosher-food">
              <Button className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl">
                {t('backToHome')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-20 pb-12">
      <div className="max-w-2xl mx-auto px-4">
        <Card className="bg-white border-[#E5E3D8] rounded-2xl" data-testid="payment-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h1 className="font-heading text-3xl font-bold text-[#0F2922] mb-2" data-testid="success-title">
              {t('paymentSuccess')}
            </h1>
            <p className="font-body text-[#5C6C62] mb-8">
              {t('paymentSuccessMessage')}
            </p>

            {order && (
              <div className="bg-[#FAF9F6] rounded-xl p-6 mb-8 text-left">
                <h3 className="font-heading font-bold text-[#0F2922] mb-4">
                  {language === 'es' ? 'Resumen del pedido' : language === 'en' ? 'Order Summary' : 'סיכום הזמנה'}
                </h3>
                <div className="space-y-2">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between font-body text-sm">
                      <span className="text-[#5C6C62]">
                        {item.quantity}x {item[`name_${language}`] || item.name_es}
                      </span>
                      <span className="text-[#0F2922] font-medium">
                        ${item.subtotal.toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-[#E5E3D8] pt-2 mt-2 flex justify-between">
                    <span className="font-heading font-bold text-[#0F2922]">{t('total')}</span>
                    <span className="font-heading font-bold text-[#B85042]">
                      ${order.total_amount.toFixed(2)} MXN
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex">
              <Link to="/" className="flex-1">
                <Button 
                  variant="outline" 
                  className="w-full border-[#E5E3D8] rounded-xl py-6"
                  data-testid="back-home-button"
                >
                  <Home className="w-5 h-5 mr-2" />
                  {t('backToHome')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
